import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Checkbox,
  Tooltip,
  IconButton as MuiIconButton,
  Alert,
  Divider,
  Chip,
} from "@mui/material";
import {
  ExpandMore,
  HelpOutline,
  Storage,
  Memory,
  Speed,
  BugReport,
  AutoFixHigh,
  Psychology,
  DataObject,
  FilterList,
  Search,
  CompareArrows,
  TextFields,
  Recommend,
  SmartToy,
  AccountTree,
  ViewModule,
  TableRows,
  TuneSharp,
  Description,
  Settings,
  CheckCircle,
  Cached,
} from "@mui/icons-material";

const KnowledgeBaseFolderDialog = ({
  open,
  onClose,
  onCreateFolder,
  isCreating = false,
}) => {
  // Basic folder information
  const [folderName, setFolderName] = useState("");
  const [folderDescription, setFolderDescription] = useState("");

  // Vector store and embedding configuration
  const [selectedVectorStore, setSelectedVectorStore] = useState("PGVector");
  const [selectedEmbeddingModel, setSelectedEmbeddingModel] = useState(
    "text-embedding-3-large"
  );
  const [useBatchMode, setUseBatchMode] = useState(true);

  // Advanced processing options
  const [chunkingStrategy, setChunkingStrategy] = useState("recursive");
  const [dataSize, setDataSize] = useState("medium");
  const [chunkSize, setChunkSize] = useState(1000);
  const [overlap, setOverlap] = useState(200);
  const [similarityThreshold, setSimilarityThreshold] = useState(0.5);
  const [agenticModel, setAgenticModel] = useState("gpt-4o-mini");
  const [maxChunkSize, setMaxChunkSize] = useState(2000);
  const [splitOnPages, setSplitOnPages] = useState(true);
  const [skipHeader, setSkipHeader] = useState(false);
  const [cleanRows, setCleanRows] = useState(true);
  const [searchType, setSearchType] = useState("hybrid");

  // Vector store options
  const vectorStoreOptions = ["PGVector"];

  // Embedding model options - only text-embedding-3-large is allowed
  const embeddingModelOptions = [
    "text-embedding-3-large",
    "mxbai-embed-large:335m"
    // "text-embedding-3-small", // Disabled
    // "text-embedding-ada-002", // Disabled
  ];

  // Chunking strategy options with detailed information
  const chunkingStrategyOptions = [
    {
      value: "fixed",
      label: "Fixed Size",
      icon: <ViewModule />,
      description: "Fixed-size chunks with optional overlap",
      detailedInfo:
        "Creates uniform chunks of exactly the specified size. Best for consistent processing and when you need predictable chunk sizes.",
      useCase: "Ideal for: Technical documentation, APIs, structured content",
      pros: [
        "Predictable chunk sizes",
        "Simple and fast",
        "Good for uniform content",
      ],
      cons: ["May break sentences/paragraphs", "Less context-aware"],
    },
    {
      value: "recursive",
      label: "Recursive",
      icon: <AccountTree />,
      description: "Recursively splits text respecting structure",
      detailedInfo:
        "Intelligently splits text by trying different separators (paragraphs, sentences, words) to maintain natural boundaries.",
      useCase: "Ideal for: General documents, articles, most text content",
      pros: [
        "Respects natural boundaries",
        "Good balance of size and context",
        "Works well for most content",
      ],
      cons: ["Variable chunk sizes", "More complex processing"],
    },
    {
      value: "semantic",
      label: "Semantic",
      icon: <Psychology />,
      description: "Groups semantically related content",
      detailedInfo:
        "Uses AI embeddings to group similar content together, creating chunks based on meaning rather than size.",
      useCase:
        "Ideal for: Research papers, complex documents, thematic content",
      pros: [
        "Maintains topic coherence",
        "Better for Q&A",
        "Contextually relevant chunks",
      ],
      cons: ["Slower processing", "Requires embeddings", "Variable sizes"],
    },
    {
      value: "agentic",
      label: "Agentic (AI-powered)",
      icon: <SmartToy />,
      description: "AI-powered intelligent chunk boundaries",
      detailedInfo:
        "Uses large language models to intelligently determine the best places to split content, understanding context and meaning.",
      useCase: "Ideal for: Complex documents, legal texts, academic papers",
      pros: [
        "Highest quality chunks",
        "Context-aware",
        "Intelligent boundaries",
      ],
      cons: ["Slowest processing", "Costs more tokens", "Requires AI model"],
    },
    {
      value: "document",
      label: "Document",
      icon: <Description />,
      description: "Optimized for structured documents",
      detailedInfo:
        "Designed for documents with clear structure like headers, sections, and hierarchies. Preserves document organization.",
      useCase: "Ideal for: PDFs, Word docs, structured reports",
      pros: [
        "Preserves structure",
        "Good for hierarchical content",
        "Document-aware",
      ],
      cons: ["Less flexible", "Requires structured input"],
    },
    {
      value: "row",
      label: "Row-based",
      icon: <TableRows />,
      description: "For CSV/tabular data (row-based chunking)",
      detailedInfo:
        "Processes tabular data row by row, perfect for CSV files and structured datasets.",
      useCase: "Ideal for: CSV files, spreadsheets, database exports",
      pros: [
        "Perfect for tabular data",
        "Preserves row integrity",
        "Fast processing",
      ],
      cons: ["Only for structured data", "Limited to CSV/table formats"],
    },
  ];

  // Data size options with detailed optimization info
  const dataSizeOptions = [
    {
      value: "small",
      label: "Small (<1MB)",
      description: "Optimized for small documents",
      settings: "Chunk Size: 500, Overlap: 100, Strategy: Fixed",
      useCase: "Perfect for: Short articles, emails, small PDFs, quick tests",
      performance: "Fast processing, minimal resources",
    },
    {
      value: "medium",
      label: "Medium (1-10MB)",
      description: "Balanced settings for medium files",
      settings: "Chunk Size: 1000, Overlap: 200, Strategy: Recursive",
      useCase: "Perfect for: Most documents, reports, books, typical use cases",
      performance: "Good balance of speed and quality",
    },
    {
      value: "large",
      label: "Large (>10MB)",
      description: "Optimized for large datasets",
      settings: "Chunk Size: 2000, Overlap: 400, Strategy: Semantic",
      useCase:
        "Perfect for: Large documents, datasets, comprehensive knowledge bases",
      performance: "Optimized for quality over speed",
    },
  ];

  // Agentic model options
  const agenticModelOptions = [
    "gpt-4o-mini",
    "gpt-4o",
    "gpt-4-turbo",
    "gpt-3.5-turbo",
  ];

  // Search type options with detailed information
  const searchTypeOptions = [
    {
      value: "vector",
      label: "Vector Search",
      icon: <Search />,
      description: "Pure semantic similarity search using AI embeddings",
      database: "Vector embeddings only",
      useCase: "Pure AI/ML semantic similarity",
      bestFor: "AI-powered content understanding, conceptual queries",
      storage: "Embedding vectors in PostgreSQL",
      storageOverhead: "Lower",
      searchCapabilities: "Semantic similarity only",
      pros: [
        "Lower storage overhead",
        "Fast semantic search",
        "Efficient for AI applications",
        "Good for conceptual queries",
      ],
      cons: [
        "No keyword matching",
        "May miss exact terms",
        "Less intuitive for users expecting keyword search",
      ],
      performance: "Fast queries, efficient storage",
    },
    {
      value: "hybrid",
      label: "Hybrid Search",
      icon: <CompareArrows />,
      description:
        "Combines semantic similarity with traditional keyword search",
      database: "Vector embeddings + full-text indexes",
      useCase: "Combines semantic similarity + keyword matching",
      bestFor: "General knowledge bases, comprehensive search",
      storage: "Embeddings + preprocessed text for keyword search",
      storageOverhead: "Higher",
      searchCapabilities: "Semantic + keyword matching",
      pros: [
        "Best of both worlds",
        "Semantic + keyword search",
        "Better recall",
        "User-friendly",
      ],
      cons: [
        "Higher storage overhead",
        "More complex processing",
        "Slightly slower indexing",
      ],
      performance: "Balanced performance, comprehensive results",
    },
    {
      value: "keyword",
      label: "Keyword Search",
      icon: <TextFields />,
      description: "Traditional keyword/phrase search with full-text indexes",
      database: "Full-text search indexes only (no vectors)",
      useCase: "Traditional keyword/phrase search",
      bestFor: "Text-heavy applications, faster storage, lower costs",
      storage: "Preprocessed text with PostgreSQL full-text search",
      storageOverhead: "Lowest",
      searchCapabilities: "Keyword and phrase matching",
      pros: [
        "Fastest storage",
        "Lowest cost",
        "Exact keyword matching",
        "Traditional search behavior",
      ],
      cons: [
        "No semantic understanding",
        "Limited to exact matches",
        "May miss related concepts",
      ],
      performance: "Very fast, minimal storage",
    },
  ];

  // Helper component for tooltips
  const InfoTooltip = ({ title, content, children, placement = "top" }) => (
    <Tooltip
      title={
        <Box sx={{ maxWidth: 350, p: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: "white" }}
          >
            {title}
          </Typography>
          {typeof content === "string" ? (
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
              {content}
            </Typography>
          ) : (
            content
          )}
        </Box>
      }
      placement={placement}
      arrow
      enterDelay={300}
      leaveDelay={200}
      sx={{
        "& .MuiTooltip-tooltip": {
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          fontSize: "0.875rem",
          maxWidth: "400px",
          padding: "12px 16px",
        },
        "& .MuiTooltip-arrow": {
          color: "rgba(0, 0, 0, 0.9)",
        },
      }}
    >
      {children}
    </Tooltip>
  );

  // Strategy recommendation helper
  const getStrategyRecommendation = (strategy) => {
    const recommendations = {
      fixed: "🎯 Good for beginners and simple documents",
      recursive: "⭐ Recommended for most use cases",
      semantic: "🧠 Best for research and complex content",
      agentic: "🚀 Premium quality for important documents",
      document: "📄 Perfect for structured PDFs and reports",
      row: "📊 Essential for CSV and tabular data",
    };
    return recommendations[strategy] || "";
  };

  // Validation function for folder name
  const validateFolderName = (name) => {
    const validNameRegex = /^[a-z0-9_]+$/;
    return validNameRegex.test(name);
  };

  // Validation function for embedding model
  const validateEmbeddingModel = (model) => {
    const allowedModels = ["text-embedding-3-large", "mxbai-embed-large:335m"];
    return allowedModels.includes(model);
  };

  // Transform input to only allow valid characters
  const handleFolderNameChange = (e) => {
    const value = e.target.value;
    // Only allow lowercase letters, numbers, and underscores
    const filteredValue = value.replace(/[^a-z0-9_]/g, "");
    setFolderName(filteredValue);
  };

  const handleCreate = () => {
    if (!folderName.trim()) {
      return;
    }

    // Validate folder name format
    if (!validateFolderName(folderName.trim())) {
      // You can add a toast notification or error state here
      console.error(
        "Folder name can only contain lowercase letters, numbers, and underscores"
      );
      return;
    }

    // Validate embedding model
    if (!validateEmbeddingModel(selectedEmbeddingModel)) {
      console.error(
        "Only text-embedding-3-large is allowed as embedding model"
      );
      return;
    }

    const folderConfig = {
      name: folderName.trim(),
      description: folderDescription.trim(),
      vectorStore: selectedVectorStore,
      embeddingModel: selectedEmbeddingModel,
      useBatchMode,
      processingOptions: {
        chunkingStrategy,
        dataSize,
        chunkSize,
        overlap,
        similarityThreshold,
        agenticModel,
        maxChunkSize,
        splitOnPages,
        skipHeader,
        cleanRows,
        searchType,
      },
    };

    onCreateFolder(folderConfig);
  };

  const handleClose = () => {
    // Reset form
    setFolderName("");
    setFolderDescription("");
    setSelectedVectorStore("PGVector");
    setSelectedEmbeddingModel("text-embedding-3-large");
    setUseBatchMode(true);
    setChunkingStrategy("recursive");
    setDataSize("medium");
    setChunkSize(1000);
    setOverlap(200);
    setSimilarityThreshold(0.5);
    setAgenticModel("gpt-4o-mini");
    setMaxChunkSize(2000);
    setSplitOnPages(true);
    setSkipHeader(false);
    setCleanRows(true);
    setSearchType("hybrid");

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "80vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          backgroundColor: "#e36250",
          color: "white",
          fontWeight: 600,
        }}
      >
        <Storage sx={{ fontSize: 24 }} />
        Create Rules book Folder
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Basic Information */}
        <Paper
          elevation={2}
          sx={{ p: 3, mb: 3, backgroundColor: "rgba(227, 98, 80, 0.05)" }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, color: "#e36250", fontWeight: 600 }}
          >
            Basic Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Folder Name"
                value={folderName}
                onChange={handleFolderNameChange}
                required
                error={
                  folderName.trim() !== "" &&
                  !validateFolderName(folderName.trim())
                }
                helperText={
                  folderName.trim() !== "" &&
                  !validateFolderName(folderName.trim())
                    ? "Name can only contain lowercase letters, numbers, and underscores"
                    : "Enter a unique name for your knowledge base folder (e.g., my_folder, data_2024, project_1)"
                }
                sx={{
                  "& .MuiInputLabel-root.Mui-focused": { color: "#e36250" },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                style={{ width: "66rem" }}
                fullWidth
                label="Description"
                value={folderDescription}
                onChange={(e) => setFolderDescription(e.target.value)}
                helperText="Optional description for your knowledge base folder"
                multiline
                rows={2}
                sx={{
                  "& .MuiInputLabel-root.Mui-focused": { color: "#e36250" },
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Vector Store and Embedding Configuration */}
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: "rgba(227, 98, 80, 0.05)",
            border: "1px solid rgba(227, 98, 80, 0.2)",
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Settings sx={{ fontSize: 20, mr: 1, color: "#e36250" }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#e36250" }}>
              Processing Configuration
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
            Select the vector store and embedding model for processing your
            knowledge base folder
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="vector-store-label">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Storage sx={{ fontSize: 18 }} />
                    Vector Store
                  </Box>
                </InputLabel>
                <Select
                  labelId="vector-store-label"
                  value={selectedVectorStore}
                  onChange={(e) => setSelectedVectorStore(e.target.value)}
                  label="Vector Store"
                >
                  {vectorStoreOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="embedding-model-label">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Memory sx={{ fontSize: 18 }} />
                    Embedding Model
                  </Box>
                </InputLabel>
                <Select
                  labelId="embedding-model-label"
                  value={selectedEmbeddingModel}
                  onChange={(e) => setSelectedEmbeddingModel(e.target.value)}
                  label="Embedding Model"
                >
                  {embeddingModelOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Batch Insert Mode Section */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              {useBatchMode ? (
                <Speed sx={{ fontSize: 20, mr: 1, color: "#4caf50" }} />
              ) : (
                <BugReport sx={{ fontSize: 20, mr: 1, color: "#ff9800" }} />
              )}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Batch Insert Mode (use_batch)
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={useBatchMode}
                  onChange={(e) => setUseBatchMode(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#4caf50",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#4caf50",
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {useBatchMode
                      ? "ON (recommended for large datasets)"
                      : "OFF (debug mode)"}
                  </Typography>
                </Box>
              }
              sx={{ mb: 2 }}
            />

            <Box sx={{ pl: 1 }}>
              {useBatchMode ? (
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 1 }}
                >
                  <strong>Data chunks are inserted in batches</strong>, making
                  ingestion much faster and more efficient. Best when uploading
                  many documents (MBs–GBs of data).
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 1 }}
                >
                  <strong>Inserts each chunk one by one</strong>. Slower, but
                  makes it easier to see which chunk fails if there's an error.
                  Best for testing with a few documents.
                </Typography>
              )}

              <Alert
                severity="info"
                sx={{
                  backgroundColor: "rgba(33, 150, 243, 0.1)",
                  border: "1px solid rgba(33, 150, 243, 0.2)",
                  "& .MuiAlert-icon": {
                    fontSize: "18px",
                  },
                }}
              >
                <Typography variant="body2">
                  💡 <strong>Tip:</strong> Keep this ON for production or bulk
                  uploads. Use OFF only when troubleshooting small test files.
                </Typography>
              </Alert>
            </Box>
          </Box>
        </Paper>

        {/* Advanced Processing Options */}
        <Accordion sx={{ mt: 3, border: "1px solid rgba(227, 98, 80, 0.2)" }}>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              backgroundColor: "rgba(227, 98, 80, 0.05)",
              "&:hover": {
                backgroundColor: "rgba(227, 98, 80, 0.1)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FilterList sx={{ fontSize: 20, mr: 1, color: "#e36250" }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#e36250" }}
              >
                Advanced Processing Options
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 3 }}>
            {/* Search Type Selection */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box
                  sx={{
                    backgroundColor: "#2196f3",
                    color: "white",
                    p: 1.5,
                    borderRadius: 2,
                    mr: 2,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Search sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#2196f3", mb: 0.5 }}
                  >
                    Search & Indexing Strategy
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary" }}
                  >
                    Choose how your data will be indexed and searched - this
                    affects storage and retrieval capabilities
                  </Typography>
                </Box>
                <InfoTooltip
                  title="Critical Decision: Search Type Impact"
                  content={
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ mb: 1, color: "rgba(255,255,255,0.9)" }}
                      >
                        This choice determines how your data will be indexed
                        and searched later. It's a crucial decision that
                        affects both storage and retrieval!
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#ffeb3b" }}
                      >
                        ⚠️ Cannot be changed after upload - choose carefully
                      </Typography>
                    </Box>
                  }
                >
                  <MuiIconButton
                    size="large"
                    sx={{
                      color: "#2196f3",
                      backgroundColor: "rgba(33, 150, 243, 0.1)",
                      "&:hover": {
                        backgroundColor: "rgba(33, 150, 243, 0.2)",
                      },
                    }}
                  >
                    <HelpOutline sx={{ fontSize: 20 }} />
                  </MuiIconButton>
                </InfoTooltip>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  "@media (min-width: 600px)": { flexWrap: "nowrap" },
                }}
              >
                {searchTypeOptions.map((option) => (
                  <Box
                    key={option.value}
                    sx={{
                      flex: { xs: "1 1 100%", sm: "1 1 33.333%" },
                      minWidth: 0,
                    }}
                  >
                    <Paper
                      elevation={searchType === option.value ? 8 : 2}
                      sx={{
                        p: 2,
                        cursor: "pointer",
                        border:
                          searchType === option.value
                            ? "3px solid #2196f3"
                            : "1px solid",
                        borderColor:
                          searchType === option.value
                            ? "#2196f3"
                            : "divider",
                        backgroundColor:
                          searchType === option.value
                            ? "rgba(33, 150, 243, 0.08)"
                            : "background.paper",
                        borderRadius: 3,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        position: "relative",
                        overflow: "hidden",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        "&:hover": {
                          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                          transform: "translateY(-4px)",
                        },
                        "&:before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background:
                            searchType === option.value
                              ? "linear-gradient(90deg, #2196f3, #1976d2)"
                              : "linear-gradient(90deg, #e0e0e0, #bdbdbd)",
                          transition: "all 0.3s ease",
                        },
                      }}
                      onClick={() => setSearchType(option.value)}
                    >
                      {/* Selection Badge */}
                      {searchType === option.value && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 16,
                            right: 16,
                            backgroundColor: "#2196f3",
                            color: "white",
                            borderRadius: "50%",
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CheckCircle sx={{ fontSize: 16 }} />
                        </Box>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor:
                              searchType === option.value
                                ? "#2196f3"
                                : "action.hover",
                            color:
                              searchType === option.value
                                ? "white"
                                : "text.secondary",
                            p: 1.5,
                            borderRadius: 2,
                            mr: 2,
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 48,
                            height: 48,
                          }}
                        >
                          {option.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              mb: 1,
                              color:
                                searchType === option.value
                                  ? "#1976d2"
                                  : "text.primary",
                            }}
                          >
                            {option.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              lineHeight: 1.6,
                              fontSize: "0.85rem",
                              mb: 2,
                            }}
                          >
                            {option.description}
                          </Typography>
                        </Box>
                        <InfoTooltip
                          title={`${option.label} Deep Dive`}
                          content={
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 2,
                                  color: "rgba(255,255,255,0.9)",
                                }}
                              >
                                <strong>Database:</strong> {option.database}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 2,
                                  color: "rgba(255,255,255,0.9)",
                                }}
                              >
                                <strong>Use Case:</strong> {option.useCase}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 2,
                                  color: "rgba(255,255,255,0.9)",
                                }}
                              >
                                <strong>Best For:</strong> {option.bestFor}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 1,
                                  color: "#4caf50",
                                  fontWeight: 600,
                                }}
                              >
                                Advantages:
                              </Typography>
                              {option.pros.map((pro, idx) => (
                                <Typography
                                  key={idx}
                                  variant="caption"
                                  sx={{
                                    color: "#4caf50",
                                    display: "block",
                                  }}
                                >
                                  ✅ {pro}
                                </Typography>
                              ))}
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 1,
                                  mb: 1,
                                  color: "#ff9800",
                                  fontWeight: 600,
                                }}
                              >
                                Considerations:
                              </Typography>
                              {option.cons.map((con, idx) => (
                                <Typography
                                  key={idx}
                                  variant="caption"
                                  sx={{
                                    color: "#ff9800",
                                    display: "block",
                                  }}
                                >
                                  ⚠️ {con}
                                </Typography>
                              ))}
                            </Box>
                          }
                          placement="top"
                        >
                          <MuiIconButton
                            size="small"
                            sx={{
                              color: "text.secondary",
                              "&:hover": {
                                color: "#2196f3",
                                backgroundColor: "rgba(33, 150, 243, 0.1)",
                              },
                            }}
                          >
                            <HelpOutline sx={{ fontSize: 18 }} />
                          </MuiIconButton>
                        </InfoTooltip>
                      </Box>

                      {/* Performance metrics */}
                      <Box
                        sx={{
                          backgroundColor:
                            searchType === option.value
                              ? "rgba(33, 150, 243, 0.1)"
                              : "action.hover",
                          borderRadius: 2,
                          p: 2,
                          border: "1px solid",
                          borderColor:
                            searchType === option.value
                              ? "rgba(33, 150, 243, 0.2)"
                              : "divider",
                          transition: "all 0.3s ease",
                          mt: "auto",
                        }}
                      >
                        <Grid container spacing={1} sx={{ mb: 1 }}>
                          <Grid item xs={6}>
                            <Box sx={{ textAlign: "center" }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Storage
                              </Typography>
                              <Chip
                                label={option.storageOverhead}
                                size="small"
                                color={
                                  option.storageOverhead === "Lowest"
                                    ? "success"
                                    : option.storageOverhead === "Lower"
                                    ? "info"
                                    : "warning"
                                }
                                sx={{
                                  mt: 0.5,
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                }}
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box sx={{ textAlign: "center" }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  color: "text.secondary",
                                  display: "block",
                                }}
                              >
                                Capabilities
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  color:
                                    searchType === option.value
                                      ? "#1976d2"
                                      : "text.primary",
                                  mt: 0.5,
                                  display: "block",
                                  fontSize: "0.7rem",
                                }}
                              >
                                {option.searchCapabilities}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontStyle: "italic",
                            fontSize: "0.75rem",
                            textAlign: "center",
                            display: "block",
                          }}
                        >
                          {option.performance}
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                ))}
              </Box>

              <Alert
                severity={
                  searchType === "vector"
                    ? "info"
                    : searchType === "hybrid"
                    ? "success"
                    : "warning"
                }
                icon={
                  searchType === "vector" ? (
                    <Cached />
                  ) : searchType === "hybrid" ? (
                    <Recommend />
                  ) : (
                    <Speed />
                  )
                }
                sx={{
                  mt: 3,
                  borderRadius: 2,
                  border: `1px solid ${
                    searchType === "vector"
                      ? "rgba(33, 150, 243, 0.3)"
                      : searchType === "hybrid"
                      ? "rgba(76, 175, 80, 0.3)"
                      : "rgba(255, 152, 0, 0.3)"
                  }`,
                  backgroundColor:
                    searchType === "vector"
                      ? "rgba(33, 150, 243, 0.08)"
                      : searchType === "hybrid"
                      ? "rgba(76, 175, 80, 0.08)"
                      : "rgba(255, 152, 0, 0.08)",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  <strong>✨ Selected:</strong>{" "}
                  {
                    searchTypeOptions.find(
                      (opt) => opt.value === searchType
                    )?.label
                  }{" "}
                  -
                  {searchType === "vector"
                    ? "🎯 Optimized for semantic search and AI applications"
                    : searchType === "hybrid"
                    ? "🚀 Recommended for most knowledge bases with comprehensive search capabilities"
                    : "⚡ Fastest and most cost-effective for traditional keyword search"}
                </Typography>
              </Alert>
            </Box>

            {/* Processing Controls Row */}
            <Grid container spacing={3}>
              {/* Chunking Strategy */}
              <Grid item xs={12} md={3}>
                <Box sx={{ position: "relative" }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="chunking-strategy-label">
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <AutoFixHigh sx={{ fontSize: 18 }} />
                        Chunking Strategy
                      </Box>
                    </InputLabel>
                    <Select
                      labelId="chunking-strategy-label"
                      value={chunkingStrategy}
                      onChange={(e) => setChunkingStrategy(e.target.value)}
                      label="Chunking Strategy"
                    >
                      {chunkingStrategyOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              width: "100%",
                            }}
                          >
                            {option.icon}
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {option.label}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary" }}
                              >
                                {option.description}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <InfoTooltip
                    title="Chunking Strategy Guide"
                    content={
                      <Box>
                        {chunkingStrategyOptions.find(
                          (opt) => opt.value === chunkingStrategy
                        ) && (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ mb: 1, color: "rgba(255,255,255,0.9)" }}
                            >
                              {
                                chunkingStrategyOptions.find(
                                  (opt) => opt.value === chunkingStrategy
                                ).detailedInfo
                              }
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#4caf50", fontWeight: 600 }}
                            >
                              {
                                chunkingStrategyOptions.find(
                                  (opt) => opt.value === chunkingStrategy
                                ).useCase
                              }
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    }
                    placement="right"
                  >
                    <MuiIconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        right: 40,
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 10,
                        color: "#e36250",
                      }}
                    >
                      <HelpOutline sx={{ fontSize: 18 }} />
                    </MuiIconButton>
                  </InfoTooltip>
                </Box>

                {/* Strategy recommendation */}
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Recommend sx={{ fontSize: 16, color: "#4caf50" }} />
                  <Typography
                    variant="caption"
                    sx={{ color: "#4caf50", fontWeight: 500 }}
                  >
                    {getStrategyRecommendation(chunkingStrategy)}
                  </Typography>
                </Box>
              </Grid>

              {/* Data Size */}
              <Grid item xs={12} md={3}>
                <Box sx={{ position: "relative" }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="data-size-label">
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <DataObject sx={{ fontSize: 18 }} />
                        Data Size
                      </Box>
                    </InputLabel>
                    <Select
                      labelId="data-size-label"
                      value={dataSize}
                      onChange={(e) => setDataSize(e.target.value)}
                      label="Data Size"
                    >
                      {dataSizeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {option.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              {option.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <InfoTooltip
                    title="Data Size Optimization"
                    content={
                      <Box>
                        {dataSizeOptions.find(
                          (opt) => opt.value === dataSize
                        ) && (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ mb: 1, color: "rgba(255,255,255,0.9)" }}
                            >
                              <strong>Auto Settings:</strong>{" "}
                              {
                                dataSizeOptions.find(
                                  (opt) => opt.value === dataSize
                                ).settings
                              }
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ mb: 1, color: "rgba(255,255,255,0.9)" }}
                            >
                              {
                                dataSizeOptions.find(
                                  (opt) => opt.value === dataSize
                                ).useCase
                              }
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#4caf50" }}
                            >
                              {
                                dataSizeOptions.find(
                                  (opt) => opt.value === dataSize
                                ).performance
                              }
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    }
                    placement="right"
                  >
                    <MuiIconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        right: 40,
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 10,
                        color: "#e36250",
                      }}
                    >
                      <HelpOutline sx={{ fontSize: 18 }} />
                    </MuiIconButton>
                  </InfoTooltip>
                </Box>
              </Grid>

              {/* Chunk Size */}
              <Grid item xs={12} md={3}>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Chunk Size"
                    type="number"
                    value={chunkSize}
                    onChange={(e) => setChunkSize(Number(e.target.value))}
                    inputProps={{ min: 100, max: 4000 }}
                    helperText="Size of each text chunk (100-4000)"
                    InputProps={{
                      endAdornment: (
                        <InfoTooltip
                          title="Chunk Size Guide"
                          content={
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, color: "rgba(255,255,255,0.9)" }}
                              >
                                Controls how much text fits in each chunk. Affects
                                processing speed and context quality.
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#ffeb3b" }}>
                                • Small (100-500): Fast, less context
                              </Typography>
                              <br />
                              <Typography variant="caption" sx={{ color: "#4caf50" }}>
                                • Medium (500-1500): Balanced (recommended)
                              </Typography>
                              <br />
                              <Typography variant="caption" sx={{ color: "#ff9800" }}>
                                • Large (1500-4000): Rich context, slower
                              </Typography>
                            </Box>
                          }
                        >
                          <MuiIconButton size="small" sx={{ color: "#e36250" }}>
                            <HelpOutline sx={{ fontSize: 16 }} />
                          </MuiIconButton>
                        </InfoTooltip>
                      ),
                    }}
                  />
                </Box>
              </Grid>

              {/* Overlap */}
              <Grid item xs={12} md={3}>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Overlap"
                    type="number"
                    value={overlap}
                    onChange={(e) => setOverlap(Number(e.target.value))}
                    inputProps={{ min: 0, max: 1000 }}
                    helperText={`Overlap between chunks (${Math.round(
                      (overlap / chunkSize) * 100
                    )}% of chunk size)`}
                    InputProps={{
                      endAdornment: (
                        <InfoTooltip
                          title="Chunk Overlap Explained"
                          content={
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, color: "rgba(255,255,255,0.9)" }}
                              >
                                Overlap ensures continuity between chunks, preventing
                                loss of context at boundaries.
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#4caf50" }}>
                                • 10-20% of chunk size is optimal
                              </Typography>
                              <br />
                              <Typography variant="caption" sx={{ color: "#ffeb3b" }}>
                                • Higher overlap = better context, more storage
                              </Typography>
                              <br />
                              <Typography variant="caption" sx={{ color: "#ff9800" }}>
                                • Zero overlap = faster, but may lose context
                              </Typography>
                            </Box>
                          }
                        >
                          <MuiIconButton size="small" sx={{ color: "#e36250" }}>
                            <HelpOutline sx={{ fontSize: 16 }} />
                          </MuiIconButton>
                        </InfoTooltip>
                      ),
                    }}
                  />
                </Box>
              </Grid>

              {/* Conditional fields based on chunking strategy */}
              {chunkingStrategy === "semantic" && (
                <Grid item xs={12} style={{ width: "100%" }}>
                  <Paper
                    sx={{
                      p: 3,
                      backgroundColor: "rgba(156, 39, 176, 0.05)",
                      border: "1px solid rgba(156, 39, 176, 0.2)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Psychology
                        sx={{ fontSize: 20, color: "#9c27b0", mr: 1 }}
                      />
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "#9c27b0" }}
                      >
                        Semantic Chunking Settings
                      </Typography>
                      <InfoTooltip
                        title="Semantic Chunking Deep Dive"
                        content={
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ mb: 1, color: "rgba(255,255,255,0.9)" }}
                            >
                              Semantic chunking uses AI embeddings to group
                              content by meaning, not just size.
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#4caf50" }}
                            >
                              ✅ Perfect for research, Q&A systems, and complex
                              documents
                            </Typography>
                            <br />
                            <Typography
                              variant="caption"
                              sx={{ color: "#ff9800" }}
                            >
                              ⚠️ Slower than other methods but produces higher
                              quality chunks
                            </Typography>
                          </Box>
                        }
                      >
                        <MuiIconButton
                          size="small"
                          sx={{ ml: 1, color: "#9c27b0" }}
                        >
                          <HelpOutline sx={{ fontSize: 18 }} />
                        </MuiIconButton>
                      </InfoTooltip>
                    </Box>

                    <Box sx={{ px: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Similarity Threshold: {similarityThreshold}
                        </Typography>
                        <Chip
                          label={
                            similarityThreshold < 0.4
                              ? "Loose grouping"
                              : similarityThreshold < 0.7
                              ? "Balanced"
                              : "Tight grouping"
                          }
                          size="small"
                          color={
                            similarityThreshold < 0.4
                              ? "warning"
                              : similarityThreshold < 0.7
                              ? "success"
                              : "info"
                          }
                        />
                      </Box>
                      <Slider
                        value={similarityThreshold}
                        onChange={(e, value) => setSimilarityThreshold(value)}
                        min={0.1}
                        max={1.0}
                        step={0.1}
                        marks={[
                          { value: 0.1, label: "0.1" },
                          { value: 0.3, label: "0.3" },
                          { value: 0.5, label: "0.5" },
                          { value: 0.7, label: "0.7" },
                          { value: 1.0, label: "1.0" },
                        ]}
                        valueLabelDisplay="auto"
                        sx={{
                          "& .MuiSlider-thumb": {
                            color: "#9c27b0",
                          },
                          "& .MuiSlider-track": {
                            color: "#9c27b0",
                          },
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mt: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          More chunks, diverse content
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          Fewer chunks, similar content
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              )}

              {chunkingStrategy === "agentic" && (
                <Grid item xs={12} style={{ width: "100%" }}>
                  <Paper
                    sx={{
                      p: 3,
                      backgroundColor: "rgba(255, 152, 0, 0.05)",
                      border: "1px solid rgba(255, 152, 0, 0.2)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <SmartToy
                        sx={{ fontSize: 20, color: "#ff9800", mr: 1 }}
                      />
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "#ff9800" }}
                      >
                        Agentic (AI-Powered) Chunking Settings
                      </Typography>
                      <InfoTooltip
                        title="Agentic Chunking - Premium Quality"
                        content={
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ mb: 1, color: "rgba(255,255,255,0.9)" }}
                            >
                              Uses advanced AI models to intelligently determine
                              the best chunk boundaries based on content
                              understanding.
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#4caf50" }}
                            >
                              🚀 Highest quality chunks for critical documents
                            </Typography>
                            <br />
                            <Typography
                              variant="caption"
                              sx={{ color: "#ff9800" }}
                            >
                              💰 Uses AI tokens - slower and more expensive
                            </Typography>
                            <br />
                            <Typography
                              variant="caption"
                              sx={{ color: "#2196f3" }}
                            >
                              🎯 Perfect for legal, academic, and complex texts
                            </Typography>
                          </Box>
                        }
                      >
                        <MuiIconButton
                          size="small"
                          sx={{ ml: 1, color: "#ff9800" }}
                        >
                          <HelpOutline sx={{ fontSize: 18 }} />
                        </MuiIconButton>
                      </InfoTooltip>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ position: "relative" }}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="agentic-model-label">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Psychology sx={{ fontSize: 18 }} />
                                AI Model
                              </Box>
                            </InputLabel>
                            <Select
                              labelId="agentic-model-label"
                              value={agenticModel}
                              onChange={(e) => setAgenticModel(e.target.value)}
                              label="AI Model"
                              style={{ width: "100%" }}
                            >
                              {agenticModelOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      width: "100%",
                                    }}
                                  >
                                    <span>{option}</span>
                                    <Chip
                                      size="small"
                                      label={
                                        option.includes("mini")
                                          ? "Fast"
                                          : option.includes("4o")
                                          ? "Premium"
                                          : option.includes("turbo")
                                          ? "Balanced"
                                          : "Legacy"
                                      }
                                      color={
                                        option.includes("mini")
                                          ? "success"
                                          : option.includes("4o")
                                          ? "primary"
                                          : option.includes("turbo")
                                          ? "info"
                                          : "default"
                                      }
                                    />
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <InfoTooltip
                            title="AI Model Selection Guide"
                            content={
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 1, color: "rgba(255,255,255,0.9)" }}
                                >
                                  Choose based on your quality vs speed needs:
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#4caf50" }}
                                >
                                  • gpt-4o-mini: Fast and cost-effective
                                </Typography>
                                <br />
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#2196f3" }}
                                >
                                  • gpt-4o: Best quality and latest features
                                </Typography>
                                <br />
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#ff9800" }}
                                >
                                  • gpt-4-turbo: Balanced performance
                                </Typography>
                              </Box>
                            }
                            placement="right"
                          >
                            <MuiIconButton
                              size="small"
                              sx={{
                                position: "absolute",
                                right: 40,
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 10,
                                color: "#ff9800",
                              }}
                            >
                              <HelpOutline sx={{ fontSize: 18 }} />
                            </MuiIconButton>
                          </InfoTooltip>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Max Chunk Size"
                          type="number"
                          value={maxChunkSize}
                          onChange={(e) =>
                            setMaxChunkSize(Number(e.target.value))
                          }
                          inputProps={{ min: 500, max: 8000 }}
                          helperText="Maximum size for AI-generated chunks"
                          InputProps={{
                            endAdornment: (
                              <InfoTooltip
                                title="Max Chunk Size for AI"
                                content={
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{ mb: 1, color: "rgba(255,255,255,0.9)" }}
                                    >
                                      The AI will create chunks up to this size, but
                                      may make them smaller for better content
                                      coherence.
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "#4caf50" }}
                                    >
                                      Recommended: 1500-3000 for most documents
                                    </Typography>
                                  </Box>
                                }
                              >
                                <MuiIconButton
                                  size="small"
                                  sx={{ color: "#ff9800" }}
                                >
                                  <HelpOutline sx={{ fontSize: 16 }} />
                                </MuiIconButton>
                              </InfoTooltip>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Alert
                      severity="info"
                      sx={{ mt: 2, backgroundColor: "rgba(255, 152, 0, 0.1)" }}
                    >
                      <Typography variant="body2">
                        <strong>Cost Estimate:</strong> This method uses ~
                        {Math.ceil(maxChunkSize / 100)} AI tokens per chunk.
                        Consider using for important documents where quality is
                        crucial.
                      </Typography>
                    </Alert>
                  </Paper>
                </Grid>
              )}

              {/* File-specific options */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <TuneSharp sx={{ fontSize: 20, color: "#2196f3", mr: 1 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#2196f3" }}
                  >
                    File-Specific Processing Options
                  </Typography>
                  <InfoTooltip
                    title="File Format Optimizations"
                    content={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, color: "rgba(255,255,255,0.9)" }}
                        >
                          These options optimize processing for specific file
                          formats:
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#4caf50" }}>
                          📄 PDF options control page-based splitting
                        </Typography>
                        <br />
                        <Typography variant="caption" sx={{ color: "#ff9800" }}>
                          📊 CSV options handle tabular data structure
                        </Typography>
                      </Box>
                    }
                  >
                    <MuiIconButton
                      size="small"
                      sx={{ ml: 1, color: "#2196f3" }}
                    >
                      <HelpOutline sx={{ fontSize: 18 }} />
                    </MuiIconButton>
                  </InfoTooltip>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Paper
                      sx={{
                        p: 2,
                        backgroundColor: "rgba(244, 67, 54, 0.05)",
                        border: "1px solid rgba(244, 67, 54, 0.2)",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Description
                          sx={{ fontSize: 18, color: "#f44336", mr: 1 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#f44336" }}
                        >
                          PDF Options
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={splitOnPages}
                            onChange={(e) =>
                              setSplitOnPages(e.target.checked)
                            }
                            sx={{
                              "&.Mui-checked": {
                                color: "#f44336",
                              },
                            }}
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <span>Split PDF on pages</span>
                            <InfoTooltip
                              title="PDF Page Splitting"
                              content={
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "rgba(255,255,255,0.9)" }}
                                  >
                                    When enabled, each PDF page becomes a separate
                                    processing unit before chunking.
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#4caf50", mt: 1, display: "block" }}
                                  >
                                    ✅ Better for structured documents with clear page
                                    boundaries
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#ff9800" }}
                                  >
                                    ⚠️ May not be ideal for continuous text across
                                    pages
                                  </Typography>
                                </Box>
                              }
                            >
                              <HelpOutline
                                sx={{ fontSize: 14, color: "text.secondary" }}
                              />
                            </InfoTooltip>
                          </Box>
                        }
                      />
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Paper
                      sx={{
                        p: 2,
                        backgroundColor: "rgba(76, 175, 80, 0.05)",
                        border: "1px solid rgba(76, 175, 80, 0.2)",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <TableRows
                          sx={{ fontSize: 18, color: "#4caf50", mr: 1 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#4caf50" }}
                        >
                          CSV/Spreadsheet Options
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={skipHeader}
                              onChange={(e) =>
                                setSkipHeader(e.target.checked)
                              }
                              sx={{
                                "&.Mui-checked": {
                                  color: "#4caf50",
                                },
                              }}
                            />
                          }
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <span>Skip CSV header</span>
                              <InfoTooltip
                                title="Skip Header Row"
                                content={
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "rgba(255,255,255,0.9)" }}
                                    >
                                      Skip the first row if it contains column headers
                                      instead of data.
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#4caf50",
                                        mt: 1,
                                        display: "block",
                                      }}
                                    >
                                      Recommended for most CSV files with headers
                                    </Typography>
                                  </Box>
                                }
                              >
                                <HelpOutline
                                  sx={{ fontSize: 14, color: "text.secondary" }}
                                />
                              </InfoTooltip>
                            </Box>
                          }
                        />

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={cleanRows}
                              onChange={(e) => setCleanRows(e.target.checked)}
                              sx={{
                                "&.Mui-checked": {
                                  color: "#4caf50",
                                },
                              }}
                            />
                          }
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <span>Clean CSV rows</span>
                              <InfoTooltip
                                title="Clean CSV Rows"
                                content={
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "rgba(255,255,255,0.9)" }}
                                    >
                                      Automatically clean and normalize CSV data
                                      (remove extra spaces, handle empty cells).
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#4caf50",
                                        mt: 1,
                                        display: "block",
                                      }}
                                    >
                                      Recommended for most CSV files to ensure data
                                      quality
                                    </Typography>
                                  </Box>
                                }
                              >
                                <HelpOutline
                                  sx={{ fontSize: 14, color: "text.secondary" }}
                                />
                              </InfoTooltip>
                            </Box>
                          }
                        />
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Configuration Summary */}
        <Alert
          severity="info"
          sx={{ mt: 2, backgroundColor: "rgba(33, 150, 243, 0.1)" }}
        >
          <Typography variant="body2">
            <strong>Selected Configuration:</strong> {searchType} search type,{" "}
            {selectedVectorStore} vector store, {selectedEmbeddingModel}{" "}
            embedding model, {chunkingStrategy} chunking strategy, batch mode{" "}
            {useBatchMode ? "enabled" : "disabled"}.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: "rgba(0, 0, 0, 0.02)" }}>
        <Button
          onClick={handleClose}
          disabled={isCreating}
          sx={{ color: "text.secondary" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={
            !folderName.trim() ||
            isCreating ||
            (folderName.trim() !== "" && !validateFolderName(folderName.trim()))
          }
          sx={{
            backgroundColor: "#e36250",
            "&:hover": { backgroundColor: "#d55a48" },
            "&:disabled": { backgroundColor: "#cccccc", color: "#666666" },
          }}
        >
          {isCreating ? "Creating..." : "Create Folder"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KnowledgeBaseFolderDialog;
